import { useContext, useMemo, useEffect, Fragment, useState } from 'react';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import PropTypes from 'prop-types';
import { ReactComponent as ArrowUpIcon } from 'assets/arrow-up.svg';
import { ReactComponent as ArrowDownIcon } from 'assets/arrow-down.svg';
import i18nContext from 'components/i18n-context';
import { PAYMENT_METHOD } from 'components/common/constants';
import getCommissionRequestModel from 'components/UnapprovedPayments/models/getCommissionRequestModel';
import { getPaymentTransactionErrorMessage } from 'components/UnapprovedPayments/utils';
import { CURRENCIES } from 'services/currenciesConstant';
import { amountFormattedValue, isCryptoProvider, getErrorMessageForAlert } from 'services/utils';
import Button from 'uikit/Button/Button';
import Alert from 'uikit/Alert/Alert';
import './MultiPaymentConfirmations.scss';

const MultiPaymentConfirmations = ({
  transactions,
  onConfirm,
  isLoading,
  customerNumber,
  setSelectedTransaction,
  paymentStore
}) => {
  const i18n = useContext(i18nContext);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (transactions) {
      const fetchCommission = async () => {
        const requestData = transactions.map((transaction) => getCommissionRequestModel(transaction));

        await paymentStore.getPreparedTransactionsCommissionsInBatch(
          customerNumber,
          requestData,
          transactions,
          setSelectedTransaction
        );
      };

      fetchCommission();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groupedByCurrency = useMemo(() => {
    return transactions.reduce((acc, transaction) => {
      const isCrypto = isCryptoProvider(transaction?.transferProvider);
      const currency = transaction.currency;

      if (isCrypto) {
        acc[transaction.id] = {
          amounts: [transaction.amount],
          commissions: [transaction?.commission],
          accounts: new Set([transaction.senderAccount]),
          recipients: new Set([transaction.counterpartyName]),
          paymentMethods: new Set([transaction.paymentMethod]),
          senderName: transaction.senderName,
          currency: transaction.currency,
          isCrypto: true
        };
      } else {
        if (!acc[currency]) {
          acc[currency] = {
            amounts: [],
            commissions: [],
            accounts: new Set(),
            recipients: new Set(),
            paymentMethods: new Set(),
            senderName: transaction.senderName,
            currency: transaction.currency,
            isCrypto: false
          };
        }
        acc[currency].amounts.push(transaction.amount);
        acc[currency].commissions.push(transaction?.commission || 0);
        acc[currency].accounts.add(transaction.senderAccount);
        acc[currency].recipients.add(`${transaction.counterpartyName}`);
        acc[currency].paymentMethods.add(transaction.paymentMethod);
        acc[currency].currency = transaction.currency;
      }
      return acc;
    }, {});
  }, [transactions]);

  const visibleTransactions = useMemo(() => {
    if (transactions.length <= 3 || isExpanded) {
      return transactions;
    }
    return transactions.slice(0, 3);
  }, [transactions, isExpanded]);

  const renderDetailRow = (label, value, valueClassName) => (
    <div className={'payment-detail-row'} key={`${label}-${value}`}>
      <div className={'payment-detail-label'}>{label}</div>
      <div className={`payment-detail-value ${valueClassName}`}>{value}</div>
    </div>
  );

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
  };

  const handleViewToggle = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={'payment-confirmation-container'}>
      <div className={'payment-confirmation-card'}>
        <div className={'payment-section'}>
          <h3 className={'section-title'}>
            {i18n.getMessage('unapprovedPayments.paymentConfirmation.paymentDetails')}
          </h3>
          {Object.entries(groupedByCurrency).map(([key, data, index]) => {
            const currencySymbol = CURRENCIES[data.currency]?.symbol || data.currency;
            return (
              <Fragment key={`${key}-${index}`}>
                {renderDetailRow(
                  i18n.getMessage('unapprovedPayments.paymentConfirmation.amount'),
                  `${currencySymbol} ${(() => {
                    const totalAmount = data.amounts.reduce((sum, amount) => sum + amount, 0);
                    return (
                      data.totalAmountDetails?.displayAmount || amountFormattedValue(totalAmount, data.isCrypto ? 8 : 2)
                    );
                  })()}`,
                  'amount-highlight'
                )}
                {renderDetailRow(
                  i18n.getMessage('unapprovedPayments.paymentConfirmation.commission'),
                  `${currencySymbol} ${(() => {
                    const totalCommission = data.commissions.reduce((sum, commission) => sum + commission, 0);
                    return (
                      data.totalCommissionDetails?.displayAmount ||
                      amountFormattedValue(totalCommission, data.isCrypto ? 8 : 2)
                    );
                  })()}`
                )}
              </Fragment>
            );
          })}
        </div>

        <div className={'section-divider'} />

        <div className={'payment-section'}>
          <h3 className={'section-title'}>
            {i18n.getMessage('unapprovedPayments.paymentConfirmation.recipientDetails')}
          </h3>

          {visibleTransactions.map((transaction, index) => {
            const isCrypto = isCryptoProvider(transaction?.transferProvider);

            return (
              <Fragment key={`${transaction.id}-${index}`}>
                {renderDetailRow(
                  i18n.getMessage('unapprovedPayments.paymentConfirmation.amount'),
                  `${transaction.amountDetails?.displayAmount || amountFormattedValue(transaction.amount, isCrypto ? 8 : 2)}`
                )}
                {isCrypto ? (
                  renderDetailRow(
                    i18n.getMessage('unapprovedPayments.paymentConfirmation.walletAddress'),
                    transaction.transactionData?.recipient?.walletAddress
                  )
                ) : (
                  <>
                    {renderDetailRow(
                      i18n.getMessage('unapprovedPayments.paymentConfirmation.name'),
                      transaction.counterpartyName
                    )}
                    {renderDetailRow(
                      i18n.getMessage('unapprovedPayments.paymentConfirmation.transferDetails'),
                      transaction.comment
                    )}
                    {transaction.paymentMethod === PAYMENT_METHOD.FASTER_PAYMENTS &&
                    !transaction.transactionData.transaction_recipient.requisites.iban ? (
                      <>
                        {renderDetailRow(
                          i18n.getMessage('unapprovedPayments.paymentConfirmation.accountNumber'),
                          transaction.transactionData.transaction_recipient.requisites.account_number
                        )}
                        {renderDetailRow(
                          i18n.getMessage('unapprovedPayments.paymentConfirmation.sortCode'),
                          transaction.transactionData.transaction_recipient.requisites.sort_code
                        )}
                      </>
                    ) : transaction.paymentMethod === PAYMENT_METHOD.SWIFT &&
                      !transaction.transactionData.transaction_recipient.requisites.iban ? (
                      renderDetailRow(
                        i18n.getMessage('unapprovedPayments.paymentConfirmation.accountNumber'),
                        transaction.transactionData.transaction_recipient.requisites.account_number
                      )
                    ) : (
                      renderDetailRow(
                        i18n.getMessage('unapprovedPayments.paymentConfirmation.account'),
                        transaction.transactionData.transaction_recipient.requisites.iban ||
                          transaction.transactionData.transaction_recipient.requisites.wallet_number
                      )
                    )}
                  </>
                )}
                {}
                {paymentStore.isErrorForPreparedTransaction(transaction.id) ? (
                  <>
                    <span className={'error-message'}>
                      {getPaymentTransactionErrorMessage(i18n, paymentStore, transaction.id)}
                    </span>
                    <div className={'section-divider error'} />
                  </>
                ) : (
                  <div className={'section-divider'} />
                )}
              </Fragment>
            );
          })}

          {transactions.length > 3 && (
            <button className={'view-toggle-button'} onClick={handleViewToggle}>
              {isExpanded ? (
                <>
                  {i18n.getMessage('unapprovedPayments.paymentConfirmation.viewLessPayments')}
                  <ArrowUpIcon className={'view-toggle-icon'} />
                </>
              ) : (
                <>
                  {i18n.getMessage('unapprovedPayments.paymentConfirmation.viewAllPayments')}
                  <ArrowDownIcon className={'view-toggle-icon'} />
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {!!paymentStore.preparedTransactionsError && (
        <div className={'error-message mb-4'}>
          {i18n.getMessage('unapprovedPayments.paymentConfirmation.error-for-some')}
        </div>
      )}

      {paymentStore.temporaryError && (
        <Alert
          type={'warning'}
          className={'multi-payment-confirmations-temporary-error-alert'}
          message={getErrorMessageForAlert(i18n, paymentStore.temporaryError)}
        />
      )}

      <div className={'payment-actions'}>
        <Button
          type={'primary'}
          isDisabled={!!paymentStore.preparedTransactionsError}
          onClick={handleConfirm}
          isLoading={isLoading}
        >
          {i18n.getMessage('unapprovedPayments.paymentConfirmation.button.confirm')}
        </Button>
      </div>
    </div>
  );
};

MultiPaymentConfirmations.propTypes = {
  transactions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      accountNumber: PropTypes.string,
      walletNumber: PropTypes.string,
      amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      commission: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.oneOf([null])]),
      currency: PropTypes.string,
      paymentMethod: PropTypes.string,
      transferProvider: PropTypes.string,
      senderName: PropTypes.string,
      senderAccount: PropTypes.string,
      counterpartyName: PropTypes.string,
      comment: PropTypes.string,
      transactionData: PropTypes.object,
      createdAt: PropTypes.string,
      updatedAt: PropTypes.string
    })
  ),
  onConfirm: PropTypes.func,
  isLoading: PropTypes.bool,
  customerNumber: PropTypes.string,
  paymentStore: MobXPropTypes.observableObject,
  setSelectedTransaction: PropTypes.func
};

export default inject((stores) => ({
  paymentStore: stores.paymentStore
}))(observer(MultiPaymentConfirmations));
